'use client';

/**
 * React adapter for the suite's served top bar (`<suite-topbar>`, loaded from
 * the hub as chrome.js). The bar's layout, right cluster, hide/show and chat
 * entry live in chrome.js — edited once for every application.
 *
 * sak serves its own React bundle with the app bar as the suite's shared
 * chrome. The bar ships the shared controls plus the identity block from the
 * session; the app has no navbar of its own — help and notifications live in
 * the bar, wired to the in-app panels below.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChartBarIcon, FolderIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useTour } from "@suite/tour";
import { useAuth } from "@/hooks/useAuth";
import { openSettings } from "@/settings/openSettings";
import { supportedLanguages } from "@/i18n";
import { HelpCenterModal } from "./help/HelpCenterModal";
import { useSakTour, useSakTourLabels } from "../tour/sakTour";

/** Heroicons cross the custom-element boundary as plain SVG markup. */
const svgOf = (Icon: React.ComponentType<{ className?: string }>) =>
  renderToStaticMarkup(<Icon className="h-4 w-4" />);

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  tr: "Türkçe",
};

interface NavButton {
  key: string;
  label: string;
  title?: string;
  icon?: string;
  active?: boolean;
  onClick?: () => void;
}

interface FileMenuSection {
  label?: string;
  items: Array<{ key?: string; type?: "theme"; label?: string; icon?: string; hint?: string; danger?: boolean; onClick?: () => void }>;
}

export interface SuiteTopbarElement extends HTMLElement {
  nav: NavButton[] | null;
  fileMenu: FileMenuSection[] | null;
  identity: { name: string; sublabel: string; avatarUrl?: string } | null;
  theme: string;
  themeModes: string[];
  languages: Array<{ code: string; label: string }>;
  language: string;
  badge: number;
  notifications?: boolean;
  helpEnabled?: boolean;
  chatActive?: boolean;
  promptPlaceholder?: string;
  settingsEnabled?: boolean;
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "suite-topbar": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & { app?: string };
    }
  }
}

export function SuiteTopbar() {
  const hostRef = useRef<SuiteTopbarElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  // The Help Center panel lives here: the bar's help button (chrome:help)
  // opens it; Ctrl+K is handled inside the modal itself.
  const [helpOpen, setHelpOpen] = useState(false);
  // Tour replay entry (previously the app navbar's user menu).
  const { start: startTour } = useTour();
  const sakTour = useSakTour();
  const sakTourLabels = useSakTourLabels();
  // Appearance lives in the shared runtime (window.SuiteTheme, shared-theme
  // plan): the bar's mode quick-pick is answered by the runtime itself, and
  // this adapter only reflects the mode into the bar below.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const isAdmin = user?.role === 'ADMIN';
  const language = i18n.resolvedLanguage ?? 'en';
  const userName = user ? String(user.name ?? '') : null;

  // sak's own surfaces as suite nav: the drive, trash and (for admins)
  // analytics. `active` follows the current route — Trash tracks ?view=trash —
  // and `chrome:nav` events route back through the router below.
  const isDashboardPath = location.pathname === '/dashboard';
  const isTrashView = new URLSearchParams(location.search).get('view') === 'trash';
  const nav: NavButton[] = [
    {
      key: '/dashboard',
      label: t('navigation.files'),
      title: 'Secure Drive',
      icon: svgOf(FolderIcon),
      active: isDashboardPath && !isTrashView,
    },
    {
      key: '/dashboard?view=trash',
      label: t('navigation.trash'),
      title: 'Trash',
      icon: svgOf(TrashIcon),
      active: isDashboardPath && isTrashView,
    },
    ...(isAdmin
      ? [
          {
            key: '/admin',
            label: t('navigation.analytics'),
            title: 'Analytics',
            icon: svgOf(ChartBarIcon),
            active: location.pathname.startsWith('/admin'),
          },
        ]
      : []),
  ];

  // The File menu reaches the drive's toolbar actions through window events —
  // the drive page listens for these. The Help section carries the entries
  // that used to live in the app navbar.
  const fileMenu: FileMenuSection[] = [
    {
      label: "Drive",
      items: [
        { key: "upload", label: "Upload Files…", onClick: () => window.dispatchEvent(new CustomEvent("sak:upload")) },
        { key: "new-folder", label: "New Folder…", onClick: () => window.dispatchEvent(new CustomEvent("sak:new-folder")) },
      ],
    },
    {
      label: t("help.fileMenuSection"),
      items: [
        { key: "help-center", label: t("help.menuItemHelpCenter"), onClick: () => setHelpOpen(true) },
        { key: "replay-tour", label: t("tour.replay"), onClick: () => startTour(sakTour, sakTourLabels) },
      ],
    },
  ];

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    el.nav = nav;
    el.fileMenu = fileMenu;
    el.identity = userName ? { name: userName, sublabel: "Sak" } : null;
    el.themeModes = ["light", "dark", "system"];
    el.languages = supportedLanguages.map((code) => ({ code, label: LANGUAGE_LABELS[code] ?? code }));
    el.language = language;
    // The bell is the notifications affordance: its badge mirrors the stored
    // list (NotificationListener keeps it fresh via sak:notifications-updated)
    // and a click comes back as chrome:bell below.
    el.badge = 0;
    try {
      const raw = localStorage.getItem('notifications');
      const list = raw ? JSON.parse(raw) : [];
      el.badge = Array.isArray(list) ? list.length : 0;
    } catch {
      el.badge = 0;
    }
    el.notifications = true;
    // The right-cluster help button opens the in-app Help Center panel.
    el.helpEnabled = true;
    // The in-app settings dialog is the default path; the shared two-column
    // settings window when the user opts in (settings-window plan).
    el.settingsEnabled = true;
    // Route changes and language switches re-sync the bar's active state.
  });

  // Mode reflection: SuiteTheme applies and persists the appearance and
  // answers the bar's unclaimed `chrome:theme` picks itself; this adapter
  // only mirrors its mode into `el.theme` — initially and on every change —
  // so the quick-pick checkmark stays in step. `el.themeModes` above keeps
  // the bar's pick list.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const api = window.SuiteTheme;
    if (!api) return;
    el.theme = api.mode;
    return api.onChange((state) => {
      el.theme = state.mode;
    });
  }, []);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    // Nav buttons carry their route as the key; anything unknown is a no-op.
    const onNav = (e: Event) => {
      const key = (e as CustomEvent<{ key?: string }>).detail?.key;
      if (key === "/dashboard" || key === "/dashboard?view=trash" || key === "/admin") {
        navigateRef.current(key);
      }
    };
    // The right-cluster language menu reports the picked code.
    const onLanguage = (e: Event) => {
      const code = (e as CustomEvent<{ code?: string }>).detail?.code;
      if (code && (supportedLanguages as readonly string[]).includes(code)) void i18n.changeLanguage(code);
    };
    // "Sign out" from the account menu: clear the session, back to login.
    const onLogout = () => {
      void logoutRef.current().then(() => navigateRef.current("/auth/login"));
    };
    // The gear button: the settings dispatcher — the in-app dialog by default,
    // the shared two-column settings window when the user opts in.
    const onSettings = () => openSettings();
    // The account menu's "Profile & Account" lands on the same settings
    // dialog — the app's one identity surface.
    const onUser = () => openSettings();
    // The right-cluster help button: open the in-app Help Center panel.
    const onHelp = () => setHelpOpen(true);
    // The cluster's lead Help Center button opens the same panel. Claiming
    // the cancelable intent (preventDefault) keeps chrome.js's windowed-modal
    // iframe fallback from opening on top of it.
    const onHelpCenter = (event?: Event) => {
      event?.preventDefault();
      setHelpOpen(true);
    };
    // The bell: open the app's notifications panel (NotificationCenter listens
    // for this window event).
    const onBell = () => window.dispatchEvent(new CustomEvent("sak:open-notifications"));
    el.addEventListener("chrome:nav", onNav);
    el.addEventListener("chrome:language", onLanguage);
    el.addEventListener("chrome:logout", onLogout);
    el.addEventListener("chrome:settings", onSettings);
    el.addEventListener("chrome:user", onUser);
    el.addEventListener("chrome:help", onHelp);
    el.addEventListener("chrome:help-center", onHelpCenter);
    el.addEventListener("chrome:bell", onBell);
    return () => {
      el.removeEventListener("chrome:nav", onNav);
      el.removeEventListener("chrome:language", onLanguage);
      el.removeEventListener("chrome:logout", onLogout);
      el.removeEventListener("chrome:settings", onSettings);
      el.removeEventListener("chrome:user", onUser);
      el.removeEventListener("chrome:help", onHelp);
      el.removeEventListener("chrome:help-center", onHelpCenter);
      el.removeEventListener("chrome:bell", onBell);
    };
  }, [i18n]);

  // The stored notification list drives the bell badge; NotificationListener
  // broadcasts every change.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const onUpdate = (e: Event) => {
      const count = (e as CustomEvent<{ count?: number }>).detail?.count;
      if (typeof count === "number") el.badge = count;
    };
    window.addEventListener("sak:notifications-updated", onUpdate as EventListener);
    return () => window.removeEventListener("sak:notifications-updated", onUpdate as EventListener);
  }, []);

  useEffect(() => {
    return () => {
      // Route changes unmount page layouts; make sure the bar releases
      // the dock's FAB suppression when it goes.
      const w = window as unknown as { OrtamDock?: { setFabVisible?: (v: boolean) => void } };
      w.OrtamDock?.setFabVisible?.(true);
    };
  }, []);

  return (
    <>
      <suite-topbar ref={hostRef} app="sak" />
      <HelpCenterModal open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}
